/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import InputGrid from "screens/leads/components/InputGrid";
import { MdFilterList, MdOutlineFilterListOff } from "react-icons/md";
import { Badge, Popover, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { usePropostasListarEtapas } from "services/api/Funil_Propostas/etapas-listar-get";
import { usePropostasFiltrosEtapas } from "services/api/Funil_Propostas/etapas-filtro-get";
import { usePropostasFiltrosSubEtapas } from "services/api/Funil_Propostas/subetapas-filtro-get";
import { useExportarKanban } from "services/api/Funil_Propostas/exportar-kanban";
import ModalExportarKanban from "screens/funilComercial/components/ModalExportarKanban";
import * as IconsSVG from "../../assets/iconsSVG";
import NavCommon from "../../components/@navHeader";
import * as S from "./styles";
import EtapaCard from "./components/EtapaCard";
import Filtro from "./components/Filtro";

interface Option {
  value: string;
  label: string;
}

const tipoFiltroDataOptions: Option[] = [
  { value: "0", label: "Data criação" },
  { value: "1", label: "Data atualização" },
];

const toApiDate = (date: Date | null) => {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd");
};

const FunilComercial = () => {
  const history = useHistory();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEtapa, setSelectedEtapa] = useState<Option | null>(null);
  const [selectedSubetapa, setSelectedSubetapa] = useState<Option[]>([]);
  const [selectedEmpreendimento, setSelectedEmpreendimento] = useState<
    Option[]
  >([]);
  const [selectedTipoFiltroData, setSelectedTipoFiltroData] =
    useState<Option | null>(null);
  const [dataInicio, setDataInicio] = useState<Date | null>(null);
  const [dataFim, setDataFim] = useState<Date | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filterAction, setFilterAction] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [etapasQuantidade, setEtapasQuantidade] = useState<
    Record<string, number>
  >({});
  const [etapasPaginaAtual, setEtapasPaginaAtual] = useState<
    Record<string, number>
  >({});

  const handleQuantidadeLoaded = useCallback(
    (codigo: string, quantidade: number, pagina: number) => {
      setEtapasQuantidade((prev) => {
        if (prev[codigo] === quantidade) return prev;
        return { ...prev, [codigo]: quantidade };
      });
      setEtapasPaginaAtual((prev) => {
        if (prev[codigo] === pagina) return prev;
        return { ...prev, [codigo]: pagina };
      });
    },
    []
  );

  const { data: dataEtapaListar } = usePropostasListarEtapas({
    comercial: true,
  });

  const etapas = useMemo(
    () =>
      dataEtapaListar?.colunas?.map((etapa: any) => ({
        codigo: etapa.codigo,
        nome: etapa.nome,
      })) || [],
    [dataEtapaListar]
  );

  const exportMutation = useExportarKanban(true);

  const handleExport = (codigosSelecionados: string[]) => {
    const maiorPagina = Math.max(
      ...codigosSelecionados.map((c) => etapasPaginaAtual[c] || 1)
    );

    exportMutation.mutate(
      {
        ListaCodigo: codigosSelecionados,
        Comercial: true,
        tipoFiltroData: selectedTipoFiltroData
          ? Number(selectedTipoFiltroData.value)
          : undefined,
        dataInicio: selectedTipoFiltroData ? toApiDate(dataInicio) : undefined,
        dataFim: selectedTipoFiltroData ? toApiDate(dataFim) : undefined,
        Etapa: selectedEtapa?.value || "",
        SubEtapa: selectedSubetapa.map((s) => s.value),
        IdEmpreendimento: selectedEmpreendimento.map((e) => e.value),
        PalavraChave: searchValue || undefined,
        PaginaAtual: maiorPagina,
        TotalItensRetorno: 3,
      },
      {
        onSettled: () => {
          setShowExportModal(false);
        },
      }
    );
  };

  const { data: dataEtapasFiltro } = usePropostasFiltrosEtapas({
    comercial: true,
  });
  const {
    data: dataSubEtapasFiltro,
    refetch: refetchSubEtapasFiltro,
    isFetching: isFetchingSubEtapasFiltro,
  } = usePropostasFiltrosSubEtapas({
    codigoEtapa: selectedEtapa?.value,
    comercial: true,
  });

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleSearch = () => {
    setFilterAction(true);
  };

  const handleEtapaChange = async (newValue: Option | null) => {
    await setSelectedEtapa(newValue);
    setSelectedSubetapa([]);
    refetchSubEtapasFiltro();
  };

  const handleSubetapaChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValues: Option[] | null
  ) => {
    if (newValues) {
      setSelectedSubetapa(newValues);
    }
  };

  const handleEmpreendimentoChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValues: Option[] | null
  ) => {
    if (newValues) {
      setSelectedEmpreendimento(newValues);
    } else {
      setSelectedEmpreendimento([]);
    }
  };

  const handleTipoFiltroDataChange = (newValue: Option | null) => {
    setSelectedTipoFiltroData(newValue);
    if (!newValue) {
      setDataInicio(null);
      setDataFim(null);
    }
  };

  const handleDataInicioChange = (newDate: Date | null) => {
    setDataInicio(newDate);
    if (newDate && dataFim && newDate > dataFim) {
      setDataFim(null);
    }
  };

  const handleDataFimChange = (newDate: Date | null) => {
    setDataFim(newDate);
    if (newDate && dataInicio && newDate < dataInicio) {
      setDataInicio(null);
    }
  };

  const handleApplyFilters = () => {
    setIsFilterApplied(true);
    setFilterAction(true);
    handleClose();
  };

  const handleClearFilters = () => {
    setSelectedEtapa(null);
    setSelectedSubetapa([]);
    setSelectedEmpreendimento([]);
    setIsFilterApplied(false);
    setSelectedEmpreendimento([]);
    setSelectedTipoFiltroData(null);
    setDataInicio(null);
    setDataFim(null);
    setFilterAction(true);
    setDataInicio(null);
    setDataFim(null);
    handleClose();
  };

  const handleClearAllFilters = () => {
    setSelectedEtapa(null);
    setSelectedSubetapa([]);
    setSearchValue("");
    setSelectedEmpreendimento([]);
    setSelectedTipoFiltroData(null);
    setDataInicio(null);
    setDataFim(null);
    setIsFilterApplied(false);
    setFilterAction(true);
    setDataInicio(null);
    setDataFim(null);
  };

  return (
    <>
      <NavCommon
        breadcrumbs={[{ path: "/funil-comercial", name: "Funil comercial" }]}
        onBack={() => {
          history.goBack();
        }}
        onChangeActionNew={() => history.push("/")}
      />
      <S.Content>
        <S.Title>Funil comercial</S.Title>
        <S.Description>Confira o funil comercial atualizado.</S.Description>
        <S.Container>
          <S.Header>
            <Tooltip title="Exportar para Excel" placement="top" arrow>
              <S.ButtonExport
                onClick={() => setShowExportModal(true)}
                disabled={etapas.length === 0}
              >
                <IconsSVG.ExportarExcelIcon />
              </S.ButtonExport>
            </Tooltip>
            <InputGrid
              style={{ width: "500px" }}
              variant="outlined"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              handleBtnSearch={() => {
                handleSearch();
              }}
            />
            <Badge
              badgeContent={
                isFilterApplied
                  ? selectedSubetapa.length > 0 ||
                    selectedEmpreendimento.length > 0 ||
                    selectedEtapa ||
                    selectedTipoFiltroData
                    ? 1
                    : 0
                  : 0
              }
              color="error"
            >
              <S.ButtonFilter type="button" onClick={handleClick}>
                <MdFilterList size={20} />
                Filtrar
              </S.ButtonFilter>
            </Badge>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Typography sx={{ p: 4 }}>
                <Filtro
                  selectedEtapa={selectedEtapa}
                  handleEtapaChange={handleEtapaChange}
                  selectedSubetapa={selectedSubetapa}
                  handleSubetapaChange={handleSubetapaChange}
                  handleClearFilters={handleClearFilters}
                  handleApplyFilters={handleApplyFilters}
                  dataEtapasFiltro={dataEtapasFiltro}
                  dataSubEtapasFiltro={dataSubEtapasFiltro}
                  isFetchingSubEtapasFiltro={isFetchingSubEtapasFiltro}
                  selectedEmpreendimento={selectedEmpreendimento}
                  handleEmpreendimentoChange={handleEmpreendimentoChange}
                  selectedTipoFiltroData={selectedTipoFiltroData}
                  handleTipoFiltroDataChange={handleTipoFiltroDataChange}
                  tipoFiltroDataOptions={tipoFiltroDataOptions}
                  dataInicio={dataInicio}
                  dataFim={dataFim}
                  handleDataInicioChange={handleDataInicioChange}
                  handleDataFimChange={handleDataFimChange}
                />
              </Typography>
            </Popover>
            <S.ButtonClearFilter type="button" onClick={handleClearAllFilters}>
              <MdOutlineFilterListOff size={20} />
            </S.ButtonClearFilter>
          </S.Header>
          <S.SpaceY space={16} />
          <S.Body>
            {dataEtapaListar?.colunas?.map((etapa: any) => (
              <EtapaCard
                key={etapa.codigo}
                selectedEtapa={selectedEtapa}
                selectedSubetapa={selectedSubetapa}
                codigoEtapa={etapa.codigo}
                nomeEtapa={etapa.nome}
                searchValue={searchValue}
                filterAction={filterAction}
                setFilterAction={setFilterAction}
                selectedEmpreendimento={selectedEmpreendimento}
                selectedTipoFiltroData={selectedTipoFiltroData}
                dataInicio={dataInicio}
                dataFim={dataFim}
                onQuantidadeLoaded={handleQuantidadeLoaded}
              />
            ))}
          </S.Body>
        </S.Container>
      </S.Content>
      <ModalExportarKanban
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        etapas={etapas}
        etapasQuantidade={etapasQuantidade}
        titulo="Exportar Funil Comercial"
        isExporting={exportMutation.isLoading}
      />
    </>
  );
};

export default FunilComercial;
